/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import DatePicker from 'react-datepicker'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import Markdown from 'react-remarkable'
import { default as fragments } from '../../fragments'


import {
  Heading,
  SvgIcon,
  KeyValueTable,
  TaskAssign,
  FileUpload,
  TextArea,
  Button,
  SpanWithData,
  ObjectFilesList,
  SelectWithData
} from '../'

import {
  decodeBase64
} from '../../utils'

const QUERY_WORKFLOWRUNSTEP = gql`query($id: ID!) {
    workflowrunstep(id: $id) {
      ...WorkflowRunStep
    }
  }
  ${fragments.workflowRunStep}
`

const MUTATION_WORKFLOWRUNSTEP = gql`
  mutation ($input: WorkflowRunStepMutationInput!) {
    workflowrunstepMutate(input: $input) {
      workflowrunstep {
        ...WorkflowRunStep
      }
    }
  }
  ${fragments.workflowRunStep}
`

const filesRef = React.createRef()

export class WorkflowRunStep extends React.Component {
  state = {
    scheduledAt: null,
    status: 'Loading',
    assignedToName: 'Loading',
    changeStatus: false,
    changeAssign: false,
    changeSchedule: false,
    uploadFile: false,
    addNote: false,
    approvalAction: false
  }

  handleScheduledAtChange = (scheduledAt) => this.setState({ scheduledAt })

  saveScheduledAt = (workflowrunstepMutate) => {
    let input = {
      id: this.props.id,
      scheduledAt: this.state.scheduledAt
    }

    workflowrunstepMutate({ variables: { input }})
      .catch(error => {
        console.error('there was an error saving the scheduled at date', error)
      })

    this.setState({ changeSchedule: false })
  }

  approve = (stateOnSuccess, status, refetch) => {
    if (status !== 'processed') {
      let body = new FormData()
      body.append('workflowrunstep', decodeBase64(this.props.id))

      fetch(`${process.env.REACT_APP_API_ROOT}/v1/workflowrunstepapprove/`, {
        method: 'post',
        headers: {
          'Authorization': 'Token ' + localStorage.getItem('token') || null
        },
        body: body
      })
      .then(data => {
        if (data.ok) {  // covers 200-range of http responses
          console.log('Task successfully approved.')
          this.setState(stateOnSuccess, refetch)
        } else {
          console.log(`There was an error approving the task.  HTTP: ${data.status} - ${data.statusText}`)
        }
        return data.json()
      })
      .catch(error => {
        console.log('There was an error approving the task.', error)
      })
    }
  }

  handleAddNote = (workflowrunstepMutate, parameters) => {
    if (parameters.note) {
      parameters.note += ' '+this.state.notes
    } else {
      parameters.note = this.state.notes
    }

    let taskInfo = {
      id: this.props.id,
      parameters: JSON.stringify(parameters)
    }

    workflowrunstepMutate({ variables: { input: taskInfo }})
    .catch((error) => {
      console.log('there was an error adding notes to task', error)
    })

    this.setState({ addNote: false })
  }

  refreshAfterFileUpload = (status, refetch) => {
    this.approve({ uploadFile: false, status: 'processed' }, status, refetch)

    filesRef.current.props.data.refetch()
  }

  render() {
    return (
      <Query query={QUERY_WORKFLOWRUNSTEP} variables={{ id: this.props.id }}>
        {({ loading, error, data, refetch }) => {
          if (loading) {
            return null
          }

          if (error) {
            return null
          }

          let task = data.workflowrunstep
          let taskParams = JSON.parse(task.parameters)
          let tableData = {
            'Status': <SpanWithData forField='WorkflowRunStepStatus' searchKey={data.workflowrunstep.status.toLowerCase()} />,
            'Assigned to': data.workflowrunstep.workflowstep.executionMethod === 'MANUAL'
              ? data.workflowrunstep.assignedTo
                ? data.workflowrunstep.assignedTo.firstName + ' ' + data.workflowrunstep.assignedTo.lastName
                : ''
              : <SpanWithData
                  forField='WorkflowStepExecutionMethod'
                  searchKey={data.workflowrunstep.workflowstep.executionMethod}
                />,
            'Scheduled at': task.scheduledAt
              ? moment(task.scheduledAt).format('ddd, DD MMM YYYY, HH:mm')
              : '',
            'Description': <Markdown>{task.description}</Markdown>,
            'Files': <ObjectFilesList ref={filesRef} objectUUID={task.id} displayDownloadIcons={true} />
          }

          if (taskParams.note) {
            tableData['Notes'] = taskParams.note
          }

          let actions = [
            <Button
              key='changeStatus'
              onClick={(e) => this.setState({
                changeStatus: true,
                changeAssign: false,
                changeSchedule: false,
                uploadFile: false,
                addNote: false,
                approvalAction: false
              })}
              disabled={this.state.changeAssign || this.state.changeSchedule || this.state.uploadFile || this.state.addNote || this.state.approvalAction}>
              Change status
            </Button>,
            <Button
              key='changeSchedule'
              onClick={(e) => this.setState({
                changeSchedule: !this.state.changeSchedule,
                changeStatus: false,
                changeAssign: false,
                uploadFile: false,
                addNote: false,
                approvalAction: false
              })}
              disabled={this.state.changeStatus || this.state.changeAssign || this.state.uploadFile || this.state.addNote || this.state.approvalAction}>
              Reschedule
            </Button>
          ]

          if (task.workflowstep.executionMethod === 'MANUAL') {
            actions.push(
              <Button
                key='changeAssignment'
                onClick={(e) => this.setState({
                  changeAssign: !this.state.changeAssign,
                  changeStatus: false,
                  changeSchedule: false,
                  uploadFile: false,
                  addNote: false,
                  approvalAction: false
                })}
                disabled={this.state.changeStatus || this.state.changeSchedule || this.state.uploadFile || this.state.addNote || this.state.approvalAction}>
                Reassign
              </Button>
            )
          }

          let parameters = JSON.parse(task.parameters)
          let filetype = null
          let fileRelationships = null

          switch (parameters.action_required) {
            case 'file':
              actions.push(
                <Button
                  key='uploadFile'
                  onClick={(e) => this.setState({
                    uploadFile: true,
                    changeStatus: false,
                    changeAssign: false,
                    changeSchedule: false,
                    addNote: false,
                    approvalAction: false
                  })}
                  disabled={this.state.changeStatus || this.state.changeAssign || this.state.changeSchedule || this.state.addNote || this.state.approvalAction}>
                  Upload file
                </Button>
              )
              filetype = parameters.filetype
              fileRelationships = parameters.relationships
              break
            case 'note':
              actions.push(
                <Button
                  key='addNote'
                  onClick={(e) => this.setState({
                    addNote: true,
                    changeStatus: false,
                    changeAssign: false,
                    changeSchedule: false,
                    uploadFile: false,
                    approvalAction: false
                  })}
                  disabled={this.state.changeStatus || this.state.changeSchedule || this.state.changeAssign || this.state.uploadFile || this.state.approvalAction}>
                  Add note
                </Button>
              )
              break
            case 'approval':
              if (task.status.toLowerCase() !== 'processed') {
                actions.push(
                  <Button
                    type='brand'
                    onClick={() => this.approve(
                      {
                        status: 'processed',
                        approvalAction: true,
                        changeStatus: false,
                        changeAssign: false,
                        changeSchedule: false,
                        uploadFile: false,
                        addNote: false
                      },
                      task.status,
                      refetch
                    )}
                    disabled={this.state.changeStatus || this.state.changeSchedule || this.state.changeAssign || this.state.uploadFile || this.state.addNote || this.state.approvalAction}>
                    Mark as approved
                  </Button>
                )
              }
              break
            default:
              break
          }

          return (
            <div className='slds-size_1-of-1 slds-medium-size_1-of-1 slds-large-size_12-of-12'>
              {/* task info section */}
              <div className='slds-grid'>
                <div className='slds-p-around_medium slds-size_2-of-3'>
                  <KeyValueTable data={tableData} />
                </div>
                <Mutation mutation={MUTATION_WORKFLOWRUNSTEP}>
                  {workflowrunstepMutate => (
                    <div className='slds-size_1-of-2 slds-p-around_x-small' style={{ background:' rgba(46, 139, 87, 0.1)' }}>
                      {/* actions section */}
                      <Heading className='slds-m-top_small' marginBottom='5'>
                        <SvgIcon
                          variant='utility'
                          symbol='custom_apps'
                          colour='light'
                          className='slds-icon slds-icon-standard-groups'
                        />
                        &nbsp;
                        Actions:
                      </Heading>
                      <div className='slds-p-left_large'>
                        {actions}
                      </div>
                      <div className='slds-p-horizontal_small slds-size_1-of-1 slds-medium-size_1-of-3 slds-large-size_4-of-12 slds-m-top_medium slds-p-around_medium'>
                        {this.state.changeStatus &&
                          <TaskStatusChange
                            task={this.props.id}
                            status={this.state.status}
                            onSave={status => this.setState({ status, changeStatus: false })}
                            onCancel={() => this.setState({ changeStatus: false })}
                            workflowrunstepMutate={workflowrunstepMutate}
                          />
                        }
                        {this.state.changeAssign &&
                          <div className='slds-p-left_large slds-text-body_small slds-text-color_weak'>
                            <b>Assigned to:</b>
                            &nbsp;
                            <TaskAssign
                              taskUUID={task.id}
                              taskResponsibility={task.workflowstep.responsibility}
                              author={localStorage.getItem('userNodeId')}
                              assignedTo={task.assignedTo ? task.assignedTo.id : ''}
                              assignedToName={this.state.assignedToName}
                              onChange={() => this.setState({ changeAssign: false })}
                            />
                          </div>
                        }
                        {this.state.changeSchedule &&
                          <div className='slds-p-left_large'>
                            <div>
                              <b>Scheduled at:</b>
                              <br />
                              <DatePicker
                                selected={this.state.scheduledAt}
                                onChange={this.handleScheduledAtChange}
                                showMonthDropdown={true}
                                showYearDropdown={true}
                                dropdownMode='select'
                                minDate={moment().add(1, 'days')}
                                showTimeSelect={true}
                                timeFormat='HH:mm'
                                dateFormat='ddd, DD MMM YYYY, HH:mm'
                              />
                            </div>
                            <div className='slds-p-top_medium'>
                              <Button minWidth={100} onClick={() => this.setState({ changeSchedule: false, scheduledAt: null })}>
                                Cancel
                              </Button>
                              &nbsp;
                              <Button type='brand' minWidth={100} onClick={() => this.saveScheduledAt(workflowrunstepMutate)}>
                                Save
                              </Button>
                            </div>
                          </div>
                        }
                        {this.state.uploadFile &&
                          <div className='slds-p-left_large'>
                            <FileUpload
                              heading='Upload required file'
                              endpoint='/v1/file/'
                              onUploaded={() => this.refreshAfterFileUpload(task.status, refetch)}
                              filetype={filetype}
                              fileRelationships={fileRelationships}
                            />
                          </div>
                        }
                        {this.state.addNote &&
                          <div className='slds-p-left_large'>
                            <TextArea
                              value={this.state.notes}
                              onChange={(e) => this.setState({ notes: e.target.value })}
                            />
                            <div className='slds-float_right slds-p-around_small'>
                              <Button onClick={() => this.handleAddNote(workflowrunstepMutate, parameters)}>
                                Save
                              </Button>
                              &nbsp;
                              <Button onClick={() => this.setState({ addNote: false })}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  )}
                </Mutation>
              </div>
            </div>
          )
        }}
      </Query>
    )
  }
}

WorkflowRunStep.propTypes = {
  id: PropTypes.string.isRequired
}


class TaskStatusChange extends React.Component {
  state = {
    status: this.props.status,
  }

  handleStatusChange = () => {
    let input = {
      id: this.props.task,
      status: this.state.status
    }
    this.props.workflowrunstepMutate({ variables: { input }})
    .then(() => {
      console.log(`successful update of status - ${this.state.status}`)
      this.props.onSave(this.state.status)
    })
    .catch(error => {
      console.log('there was an error saving the status', error)
    })
  }

  render() {
    return (
      <div className='slds-p-left_large'>
        <SelectWithData
          id='status'
          forField='WorkflowRunStepStatus'
          value={this.state.status}
          onChange={e => this.setState({ status: e.target.value })}
          fieldsToDisableArray={['processed']}
        />
        <div className='slds-p-top_medium'>
          <Button minWidth={100} onClick={this.props.onCancel}>
            Cancel
          </Button>
          <Button
            type='brand'
            minWidth={100}
            onClick={this.handleStatusChange}>
            Save
          </Button>
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/workflow-run-step/index.js